
/**
 * --- External Imports ---
 */
import { FC, useCallback, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';

/**
 * --- Internal Imports ---
 */
import { ParticipantDocument, UpdateDocumentState } from '@/apis/dtos';
import Loading from '@/components/layout/Loading';
import { DocumentVerificationEventContext } from '@/contexts/data/DocumentVerificationEventContext';
import ParticipantContext from '@/contexts/data/ParticipantContext';
import { ParticipantDocumentStateContext } from '@/contexts/data/ParticipantDocumentStateContext';
import useServiceStackClient from '@/hooks/apis/useServiceStackClient';
import useGetDocumentVerificationEvents from '@/hooks/data/useGetDocumentVerificationEvents';
import useGetParticipantByLabel from '@/hooks/data/useGetParticipantByLabel';
import useGetDocumentStatesByLabel from '@/hooks/data/useGetDocumentStatesByLabel';
import { ErrorContent } from '@/pages/ErrorPage';


const ParticipantProvider: FC = () => {
    const client = useServiceStackClient();

    const { participantCode } = useParams();

    // -- Query for participant
    const participantQuery = useGetParticipantByLabel(participantCode ? { label: participantCode } : undefined, { revalidateOnFocus: false });
    const participantContext = useMemo(() => participantQuery, [participantQuery]);

    // -- Query for event forms and document details
    const documentVerificationEventQuery = useGetDocumentVerificationEvents(participantCode ? { label: participantCode } : undefined, { revalidateOnFocus: false });
    const documentVerificationContext = useMemo(() => {
        return documentVerificationEventQuery;
    }, [documentVerificationEventQuery]);

    // -- Query for document verification states
    const documentStatesQuery = useGetDocumentStatesByLabel(
        participantCode ? { participantLabel: participantCode } : undefined,
        { revalidateOnFocus: false }
    );

    const updateDocumentState = useCallback(async (state: ParticipantDocument, redactedFields?: string[], comments?: string) => {
        // Update document state
        var request = new UpdateDocumentState();
        request.participantDocument = state;
        request.documentVerificationComments = comments;
        request.redactedFields = redactedFields;
        
        const res = await client.post(request);

        // Update the list of document states
        await documentStatesQuery.refresh();

        return res;
    }, [client, documentStatesQuery]);

    const documentStateContext = useMemo(() => {
        return {
            ...documentStatesQuery,
            updateDocumentState
        };
    }, [documentStatesQuery, updateDocumentState]);
    
    const loading = participantQuery.loading || documentVerificationEventQuery.loading || documentStatesQuery.loading;
    const validating = participantQuery.validating || documentVerificationEventQuery.validating || documentStatesQuery.validating;

    return (
        <>
            <Loading name='participant-provider' isLoading={loading} isValidating={validating} />
            <ParticipantContext.Provider value={participantContext}>
                <DocumentVerificationEventContext.Provider value={documentVerificationContext}>
                    <ParticipantDocumentStateContext.Provider value={documentStateContext}>
                        {
                            participantQuery.error ?
                                <ErrorContent errorType={participantQuery.error.errorCode ?? "500"} errorMessage={participantQuery.error.message} /> :
                                <Outlet />
                        }
                    </ParticipantDocumentStateContext.Provider>
                </DocumentVerificationEventContext.Provider>
            </ParticipantContext.Provider>
        </>
    )
}

export default ParticipantProvider;